import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Crosshair, Clock, MapPin, Users, ChevronRight, X, 
  Swords, Play, Boxes, Target, Crown
} from 'lucide-react';
import { THEMES, getColorClasses } from '../../pages/themeConfig';
import { getMissions } from '../missions/missionProfiles';
import { useMissionSelection, useSquadSelection } from './MissionControl.hooks';
import {
  getDifficultyColor,
  getRiskColor,
  getReadySquadMembers,
  convertDurationToDays,
  getMissionAvailability,
  validateDeployment,
  createDeploymentPayload,
  prepareManualDeployment,
} from './MissionControl.utils';
import { MISSION_TIERS, MISSION_MODES, DEPLOYMENT_STATUS } from './MissionControl.constants';

const MissionControl = ({ 
  squad, 
  resources, 
  onDeploySquad, 
  threatLevel = 3, 
  currentCampaign, 
  theme = 'vanilla',
  activeMissions = []
}) => {
  const navigate = useNavigate();
  const config = THEMES[theme] || THEMES.vanilla;
  
  const {
    selectedMission,
    setSelectedMission,
    missionTier,
    handleTierChange,
    complexMissionMode,
    setComplexMissionMode,
    resetSelection
  } = useMissionSelection();

  const {
    selectedSquadMembers,
    toggleSquadMember,
    clearSelection: clearSquadSelection
  } = useSquadSelection();

  const missions = getMissions(config);
  const currentMissions = missions[missionTier] || [];
  const labels = config.missions || {};

  const handleDeployAuto = () => {
    if (!validateDeployment(selectedMission, selectedSquadMembers)) return;
    
    const payload = createDeploymentPayload(
      selectedMission,
      selectedSquadMembers,
      currentCampaign,
      MISSION_MODES.AUTO
    );

    onDeploySquad?.(payload);
    resetSelection();
    clearSquadSelection();
  };

  const handleDeployManual = () => {
    if (!validateDeployment(selectedMission, selectedSquadMembers)) return;
    
    const { path, state } = prepareManualDeployment(
      selectedMission,
      selectedSquadMembers,
      currentCampaign,
      theme
    );
    
    navigate(path, { state });
  };

  const handleResetSelection = () => {
    resetSelection();
    clearSquadSelection();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${getColorClasses(config.colors.primary, 'text')}`}>
            {labels.title || 'Mission Control'}
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {labels.subtitle || 'Deploy your squad on tactical operations'}
          </p>
        </div>
        <div className="flex items-center gap-2 bg-red-900/30 border-2 border-red-500 rounded-lg px-4 py-2">
          <AlertTriangle className="text-red-400" size={20} />
          <div>
            <p className="text-xs text-gray-400">{labels.threatLabel || 'THREAT LEVEL'}</p>
            <p className="font-bold text-red-400">{threatLevel}/10</p>
          </div>
        </div>
      </div>

      {/* Active Missions Tracker */}
      {activeMissions.length > 0 && (
        <div className={`bg-gray-800/50 border-2 ${getColorClasses(config.colors.secondary, 'border')} rounded-lg p-4 mb-6`}>
          <h3 className={`text-lg font-bold mb-3 flex items-center gap-2 ${getColorClasses(config.colors.secondary, 'text')}`}>
            <Clock size={20} />
            {labels.activeLabel || 'Active Missions'}
          </h3>
          <div className="space-y-2">
            {activeMissions.map(mission => (
              <div key={mission.id} className="bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className={getColorClasses(config.colors.secondary, 'text')} size={16} />
                  <div>
                    <p className="font-bold text-sm">{mission.name}</p>
                    <p className="text-xs text-gray-400">
                      {mission.squad?.length || 0} {labels.deployedLabel || 'soldiers deployed'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {mission.status === DEPLOYMENT_STATUS.COMPLETED ? (
                    <span className={`font-bold ${mission.success ? 'text-green-400' : 'text-red-400'}`}>
                      {mission.success ? `✓ ${labels.successLabel || 'SUCCESS'}` : `✗ ${labels.failedLabel || 'FAILED'}`}
                    </span>
                  ) : (
                    <>
                      <p className={`font-bold ${getColorClasses(config.colors.secondary, 'text')}`}>
                        {mission.days_remaining} {mission.days_remaining === 1 ? 'Day' : 'Days'}
                      </p>
                      <p className="text-xs text-gray-400">Remaining</p>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mission Tier Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { tier: MISSION_TIERS.SIMPLE, icon: Boxes, label: labels.simpleTier || 'SIMPLE MISSIONS' },
          { tier: MISSION_TIERS.COMPLEX, icon: Target, label: labels.complexTier || 'COMPLEX MISSIONS' },
          { tier: MISSION_TIERS.CAMPAIGN, icon: Crown, label: labels.campaignTier || 'CAMPAIGN MISSIONS' }
        ].map(({ tier, icon: Icon, label }) => (
          <button
            key={tier}
            onClick={() => handleTierChange(tier)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all ${
              missionTier === tier
                ? `${getColorClasses(config.colors.primary, 'bg')} text-white`
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>

      {/* Mission Selection */}
      {!selectedMission ? (
        <div className="grid grid-cols-2 gap-4">
          {currentMissions.map(mission => {
            const MissionIcon = mission.icon || Target;
            const availability = getMissionAvailability(mission, squad, currentCampaign?.day || 0);
            const daysRequired = convertDurationToDays(mission.duration);

            const badgeConfig = {
              [MISSION_TIERS.SIMPLE]: { className: 'bg-green-900/20 border-green-500 text-green-400', label: labels.autoOnly || 'AUTO ONLY' },
              [MISSION_TIERS.COMPLEX]: { className: 'bg-yellow-900/20 border-yellow-500 text-yellow-400', label: labels.autoOrManual || 'AUTO OR MANUAL' },
              [MISSION_TIERS.CAMPAIGN]: { className: 'bg-red-900/20 border-red-500 text-red-400', label: labels.manualOnly || 'MANUAL ONLY' }
            }[mission.tier];

            return (
              <button
                key={mission.id}
                onClick={() => availability.canSelect && setSelectedMission(mission)}
                disabled={!availability.canSelect}
                className={`text-left bg-gray-800 border-2 rounded-lg p-6 transition-all ${
                  availability.canSelect
                    ? `border-gray-700 hover:border-${mission.color}-500 hover:bg-gray-750` 
                    : 'border-gray-700 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`bg-${mission.color}-900/30 border border-${mission.color}-500 p-3 rounded-lg`}>
                    <MissionIcon className={`text-${mission.color}-400`} size={24} />
                  </div>
                  <div className={`${getDifficultyColor(mission.tier, mission.color)} px-3 py-1 rounded-full text-xs font-bold border`}>
                    {mission.tier.toUpperCase()}
                  </div>
                </div>

                <h3 className="text-xl font-bold mb-2">{mission.name}</h3>
                
                <div className="flex items-center gap-2 mb-3 text-sm text-gray-400">
                  <MapPin size={14} />
                  <span>{mission.location}</span>
                  <span className="mx-1">•</span>
                  <Clock size={14} />
                  <span>{daysRequired} {daysRequired === 1 ? 'Day' : 'Days'}</span>
                </div>

                <p className="text-sm text-gray-400 mb-4 line-clamp-2">{mission.description}</p>

                <div className={`${badgeConfig.className} border rounded px-2 py-1 text-xs inline-block mb-3`}>
                  {badgeConfig.label}
                </div>

                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">{labels.squadRequirement || 'SQUAD REQUIREMENT'}</p>
                  <div className="flex items-center gap-2">
                    <Users size={16} className={getColorClasses(config.colors.secondary, 'text')} />
                    <span className="text-sm font-bold">
                      {mission.requirements.minSquad}-{mission.requirements.maxSquad} {labels.soldiersLabel || 'soldiers'}
                    </span>
                  </div>
                </div>

                {!availability.isUnlocked && (
                  <div className="bg-red-900/20 border border-red-500 rounded px-3 py-2 text-xs text-red-400">
                    🔒 {labels.unlocksLabel || 'Unlocks on Day'} {mission.unlockDay}
                  </div>
                )}

                {!availability.isAvailable && availability.isUnlocked && (
                  <div className="bg-red-900/20 border border-red-500 rounded px-3 py-2 text-xs text-red-400">
                    ⚠️ {availability.reason}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        /* Mission Deployment Panel */
        <div className="space-y-6">
          {/* Mission Details */}
          <div className={`bg-gray-800 border-2 border-${selectedMission.color}-500 rounded-lg p-6`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-2xl font-bold">{selectedMission.name}</h3>
                  <div className={`${getDifficultyColor(selectedMission.tier, selectedMission.color)} px-3 py-1 rounded-full text-xs font-bold border`}>
                    {selectedMission.tier.toUpperCase()}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <div className="flex items-center gap-1">
                    <MapPin size={14} />
                    <span>{selectedMission.location}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <span>{convertDurationToDays(selectedMission.duration)} {labels.daysLabel || 'days'}</span>
                  </div>
                </div>
              </div>
              <button onClick={handleResetSelection} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <p className="text-gray-400 mb-4">{selectedMission.description}</p>
            
            {selectedMission.storyContext && (
              <div className={`bg-gray-700/50 border-2 ${getColorClasses(config.colors.secondary, 'border')} rounded-lg p-4 mb-4`}>
                <p className={`text-sm italic ${getColorClasses(config.colors.secondary, 'text')}`}>
                  "{selectedMission.storyContext}"
                </p>
              </div>
            )}

            {/* Rewards & Risks */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500 mb-2">{labels.rewardsLabel || 'REWARDS'}</p>
                <div className="space-y-1 text-sm">
                  {selectedMission.rewards.funds && <p className="text-green-400">💰 ${selectedMission.rewards.funds.toLocaleString()}</p>}
                  {selectedMission.rewards.intel && <p className={getColorClasses(config.colors.secondary, 'text')}>📡 {selectedMission.rewards.intel} Intel</p>}
                  {selectedMission.rewards.supplies && <p className="text-yellow-400">📦 {selectedMission.rewards.supplies} Supplies</p>}
                  <p className="text-purple-400">⭐ {selectedMission.rewards.xp} XP</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-2">{labels.risksLabel || 'RISKS'}</p>
                <div className="space-y-1 text-sm">
                  <p className={getRiskColor(selectedMission.risks.casualty)}>☠️ Casualty: {selectedMission.risks.casualty}</p>
                  <p className={getRiskColor(selectedMission.risks.casualty)}>🎯 Success: {selectedMission.risks.successRate}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mode Selection for Complex Missions */}
          {selectedMission.tier === MISSION_TIERS.COMPLEX && !complexMissionMode && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">{labels.chooseModeLabel || 'Choose Mission Mode'}</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setComplexMissionMode(MISSION_MODES.AUTO)}
                  className={`bg-gray-700 hover:bg-gray-600 border-2 border-gray-600 hover:border-${config.colors.secondary || 'blue'}-500 rounded-lg p-6 transition-all`}
                >
                  <Clock className={getColorClasses(config.colors.secondary, 'text')} size={32} />
                  <h4 className="font-bold mb-2 mt-3">{labels.autoResolveTitle || 'AUTO RESOLVE'}</h4>
                  <p className="text-sm text-gray-400">{labels.autoResolveDesc || 'Mission resolves automatically when the day advances.'}</p>
                </button>
                <button
                  onClick={() => setComplexMissionMode(MISSION_MODES.MANUAL)}
                  className={`bg-gray-700 hover:bg-gray-600 border-2 border-gray-600 hover:border-${config.colors.primary || 'red'}-500 rounded-lg p-6 transition-all`}
                >
                  <Crosshair className={getColorClasses(config.colors.primary, 'text')} size={32} />
                  <h4 className="font-bold mb-2 mt-3">{labels.manualControlTitle || 'MANUAL CONTROL'}</h4>
                  <p className="text-sm text-gray-400">{labels.manualControlDesc || 'Full tactical control on the battlefield. Higher risk, higher reward.'}</p>
                </button>
              </div>
            </div>
          )}

          {/* Squad Selection */}
          {(selectedMission.tier === MISSION_TIERS.SIMPLE || 
            selectedMission.tier === MISSION_TIERS.CAMPAIGN || 
            (selectedMission.tier === MISSION_TIERS.COMPLEX && complexMissionMode)) && (
            <>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">{labels.selectSquadLabel || 'Select Squad Members'}</h3>
                  <div className="text-sm">
                    <span className={`font-bold ${validateDeployment(selectedMission, selectedSquadMembers) ? 'text-green-400' : 'text-red-400'}`}>
                      {selectedSquadMembers.length}
                    </span>
                    <span className="text-gray-400">
                      /{selectedMission.requirements.minSquad}-{selectedMission.requirements.maxSquad} {labels.requiredLabel || 'required'}
                    </span>
                  </div>
                </div>

                <div className="grid gap-3">
                  {getReadySquadMembers(squad).map(soldier => {
                    const isSelected = selectedSquadMembers.find(s => s.id === soldier.id);
                    
                    return (
                      <button
                        key={soldier.id}
                        onClick={() => toggleSquadMember(soldier)}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? `bg-${config.colors.secondary || 'blue'}-900/30 border-${config.colors.secondary || 'blue'}-500` 
                            : 'bg-gray-700 border-gray-600 hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? `bg-${config.colors.secondary || 'blue'}-600` : 'bg-gray-600'}`}>
                            <Users size={20} className="text-white" />
                          </div>
                          <div className="text-left">
                            <p className="font-bold">{soldier.name}</p>
                            <p className="text-sm text-gray-400">{soldier.rank}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="w-24 bg-gray-600 rounded-full h-2 mb-1">
                              <div className="h-2 rounded-full bg-green-500" style={{ width: `${soldier.health}%` }} />
                            </div>
                            <p className="text-xs text-gray-400">{soldier.health}% HP</p>
                          </div>
                          {isSelected && (
                            <div className={`bg-${config.colors.secondary || 'blue'}-600 rounded-full w-6 h-6 flex items-center justify-center`}>
                              <span className="text-white text-xs font-bold">✓</span>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {getReadySquadMembers(squad).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="mx-auto mb-2" size={48} />
                    <p>{labels.noSoldiersLabel || 'No soldiers available for deployment'}</p>
                  </div>
                )}
              </div>

              {/* Deploy Buttons */}
              <div className="flex gap-4">
                {(selectedMission.tier === MISSION_TIERS.SIMPLE || 
                  (selectedMission.tier === MISSION_TIERS.COMPLEX && complexMissionMode === MISSION_MODES.AUTO)) && (
                  <button
                    onClick={handleDeployAuto}
                    disabled={!validateDeployment(selectedMission, selectedSquadMembers)}
                    className={`flex-1 py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                      validateDeployment(selectedMission, selectedSquadMembers)
                        ? `${getColorClasses(config.colors.secondary, 'bg')} hover:opacity-80 text-white`
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Play size={24} />
                    {labels.deployAutoButton || 'DEPLOY (AUTO)'}
                  </button>
                )}
                
                {(selectedMission.tier === MISSION_TIERS.CAMPAIGN || 
                  (selectedMission.tier === MISSION_TIERS.COMPLEX && complexMissionMode === MISSION_MODES.MANUAL)) && (
                  <button
                    onClick={handleDeployManual}
                    disabled={!validateDeployment(selectedMission, selectedSquadMembers)}
                    className={`flex-1 py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                      validateDeployment(selectedMission, selectedSquadMembers)
                        ? `${getColorClasses(config.colors.primary, 'bg')} hover:opacity-80 text-white`
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Swords size={24} />
                    {labels.deployManualButton || 'DEPLOY TO BATTLE'}
                    <ChevronRight size={24} />
                  </button>
                )}
                
                <button
                  onClick={handleResetSelection}
                  className="px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold transition-all"
                >
                  {labels.cancelButton || 'CANCEL'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MissionControl;